import type { MailAttachment } from "@/lib/api";

/** Strip angle brackets / whitespace and decode %40-style cid tokens. */
export function normalizeContentId(value: string | undefined | null): string {
  if (!value) return "";
  let id = String(value).trim();
  if (!id) return "";
  if (id.toLowerCase().startsWith("cid:")) id = id.slice(4);
  id = id.replace(/^<|>$/g, "").trim();
  try {
    id = decodeURIComponent(id);
  } catch {
    /* keep raw */
  }
  return id.toLowerCase();
}

/** Prefer a browser-loadable URL already present on the attachment. */
export function attachmentDisplayUrl(file: MailAttachment): string {
  return file.dataUrl || file.url || file.downloadUrl || "";
}

/**
 * Map Content-ID → display URL for attachments that already carry a URL.
 * Keys are normalized (no brackets, lowercased).
 */
export function buildCidUrlMap(attachments: MailAttachment[] | undefined | null): Map<string, string> {
  const map = new Map<string, string>();
  if (!Array.isArray(attachments)) return map;
  for (const file of attachments) {
    const href = attachmentDisplayUrl(file);
    if (!href) continue;
    const cid = normalizeContentId(file.contentId);
    if (cid) map.set(cid, href);
    // Also key by filename so Outlook cid:image001.png@HOST rewrites when URL is present.
    const name = (file.filename || "").trim().toLowerCase();
    if (name) map.set(name, href);
  }
  return map;
}

/**
 * Outlook / Word often embeds as cid:image001.png@01DD4EAB.603EB780 while the
 * MIME part filename is image001.png and Content-ID may be omitted from the API.
 */
export function cidMatchesFilename(cid: string, filename: string | undefined | null): boolean {
  const want = normalizeContentId(cid);
  const name = (filename || "").trim().toLowerCase();
  if (!want || !name) return false;
  if (want === name) return true;
  // cid:image001.png@HOST → filename image001.png
  if (want.startsWith(`${name}@`)) return true;
  // Rare: filename already includes @host
  if (name.includes("@") && want === normalizeContentId(name)) return true;
  return false;
}

export function findAttachmentByContentId(
  attachments: MailAttachment[] | undefined | null,
  contentId: string,
): MailAttachment | undefined {
  const want = normalizeContentId(contentId);
  if (!want || !Array.isArray(attachments)) return undefined;
  const byCid = attachments.find((file) => normalizeContentId(file.contentId) === want);
  if (byCid) return byCid;
  // Tip 127: Lightsail/Gmail parts often lack contentId — match Outlook cid to filename.
  return attachments.find((file) => cidMatchesFilename(want, file.filename));
}

/**
 * For unresolved cids with no contentId and no filename match, bind leftover
 * image/* parts in document order (common when API strips both Content-ID and
 * useful names). Each attachment is used at most once.
 */
export function bindOrphanCidsToImageAttachments(
  unresolvedCids: string[],
  attachments: MailAttachment[] | undefined | null,
  alreadyMapped: Map<string, string> | Set<string>,
): Map<string, MailAttachment> {
  const out = new Map<string, MailAttachment>();
  if (!unresolvedCids.length || !Array.isArray(attachments)) return out;
  const usedIds = new Set<string>();
  const mapped =
    alreadyMapped instanceof Map ? new Set(alreadyMapped.keys()) : alreadyMapped;

  for (const file of attachments) {
    const cid = normalizeContentId(file.contentId);
    if (cid && (mapped.has(cid) || attachmentDisplayUrl(file))) {
      if (file.id) usedIds.add(file.id);
      else usedIds.add(`cid:${cid}`);
    }
  }

  const pool = attachments.filter((file) => {
    const mime = (file.mimeType || "").toLowerCase();
    const name = (file.filename || "").toLowerCase();
    const looksImage =
      mime.startsWith("image/") ||
      /\.(png|jpe?g|gif|webp|bmp|heic|heif|tiff?)$/i.test(name);
    if (!looksImage) return false;
    if (file.id && usedIds.has(file.id)) return false;
    const cid = normalizeContentId(file.contentId);
    if (cid && usedIds.has(`cid:${cid}`)) return false;
    // Prefer parts that are not already exact-mapped via contentId in alreadyMapped
    if (cid && mapped.has(cid)) return false;
    return true;
  });

  let poolIdx = 0;
  for (const cid of unresolvedCids) {
    const want = normalizeContentId(cid);
    if (!want || mapped.has(want) || out.has(want)) continue;
    // Filename match first (deterministic) even inside orphan binder
    const byName = attachments.find(
      (file) => cidMatchesFilename(want, file.filename) && !usedIds.has(file.id || `name:${file.filename}`),
    );
    if (byName) {
      out.set(want, byName);
      usedIds.add(byName.id || `name:${byName.filename}`);
      continue;
    }
    while (poolIdx < pool.length) {
      const file = pool[poolIdx++]!;
      const key = file.id || `name:${file.filename || poolIdx}`;
      if (usedIds.has(key)) continue;
      out.set(want, file);
      usedIds.add(key);
      break;
    }
  }
  return out;
}

/**
 * Rewrite cid: image sources (and CSS url(cid:…)) to real attachment URLs.
 * Leaves unknown cids untouched so an async resolver can still fetch them.
 */
export function rewriteCidInHtml(
  html: string,
  cidMap: Map<string, string> | Record<string, string>,
): string {
  if (!html || !/cid:/i.test(html)) return html;
  const map =
    cidMap instanceof Map
      ? cidMap
      : new Map(
          Object.entries(cidMap).map(([key, value]) => [normalizeContentId(key), value]),
        );
  if (!map.size) return html;

  const lookup = (raw: string): string | undefined => {
    const id = normalizeContentId(raw);
    if (!id) return undefined;
    const hit = map.get(id);
    if (hit) return hit;
    // Outlook: cid:image001.png@01DD4EAB.603EB780 keyed as image001.png
    const at = id.indexOf("@");
    if (at > 0) {
      const byName = map.get(id.slice(0, at));
      if (byName) return byName;
    }
    return undefined;
  };

  let out = html.replace(
    /\b(src|SRC)\s*=\s*(["'])\s*cid:([^"']+)\2/gi,
    (full, attr: string, quote: string, cid: string) => {
      const href = lookup(cid);
      if (!href) return full;
      return `${attr}=${quote}${href}${quote}`;
    },
  );

  out = out.replace(/url\(\s*(['"]?)cid:([^)'"]+)\1\s*\)/gi, (full, quote: string, cid: string) => {
    const href = lookup(cid);
    if (!href) return full;
    const q = quote || '"';
    return `url(${q}${href}${q})`;
  });

  return out;
}

/** Pull remaining cid: tokens from HTML (after a sync rewrite). */
export function listUnresolvedCids(html: string): string[] {
  if (!html || !/cid:/i.test(html)) return [];
  const found = new Set<string>();
  const re = /(?:src\s*=\s*["']\s*cid:([^"']+)["']|url\(\s*['"]?cid:([^)'"]+)['"]?\s*\))/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html))) {
    const raw = match[1] || match[2] || "";
    const id = normalizeContentId(raw);
    if (id) found.add(id);
  }
  return [...found];
}

/** Coerce provider aliases onto MailAttachment.contentId. */
export function coerceAttachmentContentId(file: MailAttachment & Record<string, unknown>): string | undefined {
  const raw =
    file.contentId ??
    file.content_id ??
    file.contentID ??
    file.cid ??
    file.ContentId ??
    file.ContentID;
  if (typeof raw !== "string") return file.contentId;
  const trimmed = raw.trim();
  return trimmed || undefined;
}
